import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { RefreshCw } from "lucide-react";
import LoginPage from "@/pages/Login";
import ChatPage from "@/pages/Chat";
import AuthCallback from "@/pages/AuthCallback";
import { getToken } from "@/lib/api";

const RequireAuth = ({ children }) => {
  const t = getToken();
  if (!t) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const t = getToken();
  if (t) return <Navigate to="/" replace />;
  return children;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Synchronously detect Emergent OAuth redirect (hash contains session_id) before ProtectedRoute runs.
function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <LoginPage />
          </PublicOnly>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <ChatPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // Listen for service-worker update prompts dispatched from index.js
  // and show a friendly, dismissible toast instead of silently reloading.
  const shownRef = useRef(false);
  useEffect(() => {
    const onUpdate = (e) => {
      if (shownRef.current) return;
      shownRef.current = true;
      const apply = e.detail?.apply;
      toast(
        (
          <div className="flex items-start gap-2.5" data-testid="sw-update-toast">
            <RefreshCw className="h-4 w-4 mt-0.5 text-blue-300 shrink-0" />
            <div className="min-w-0 text-sm leading-snug">
              <div className="font-medium">A new version of Glitch is ready.</div>
              <div className="text-white/60 text-xs mt-0.5">
                Reload to pick up the latest fixes.
              </div>
            </div>
          </div>
        ),
        {
          id: "glitch-sw-update",
          duration: Infinity,
          action: {
            label: "Reload",
            onClick: () => {
              try {
                apply?.();
              } catch {
                window.location.reload();
              }
            },
          },
          cancel: {
            label: "Later",
            onClick: () => {
              shownRef.current = false;
            },
          },
        }
      );
    };
    window.addEventListener("glitch-sw-update", onUpdate);
    return () => window.removeEventListener("glitch-sw-update", onUpdate);
  }, []);

  return (
    <BrowserRouter>
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "rgba(10, 12, 20, 0.9)",
            border: "1px solid rgba(59,130,246,0.25)",
            color: "#fff",
            backdropFilter: "blur(12px)",
          },
        }}
      />
      <AppRouter />
    </BrowserRouter>
  );
}
